import React, { useState, useEffect, useCallback } from 'react';
import { DisabledHeaderDots } from '../../components/shared/HeaderDots';
import { useUserRoleName } from '../../hooks/useUserRoleName';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Checkbox } from "../../components/ui/checkbox";
import { ClaimViewer } from './ClaimViewer';
import { Pagination, usePagination } from './Pagination';
import BillingDeadlineBadge from '../BillingDeadlineBadge';
import { ReversalWorkQueueCard } from './ReversalWorkQueueCard'; // eslint-disable-line no-unused-vars
import { UnappliedPayerCreditsCard } from './UnappliedPayerCreditsCard'; // eslint-disable-line no-unused-vars
import { MultiSelectFilter } from '../shared/MultiSelectFilter';
import { DateField } from './DateTimeField';
import {
  RefreshCw,
  FileText,
  Sparkles,
  Eye,
  Send,
  CreditCard,
  Receipt,
  XCircle,
  List,
  LayoutGrid,
  Code,
  AlertTriangle,
  ArrowUpDown,
  ArrowLeftRight,
  Search,
  ArrowUp,
  ArrowDown,
  CornerDownRight,
  CornerDownLeft,
  Pencil,
  ChevronDown,
  ChevronUp,
  Filter,
  Check,
  ChevronsUpDown,
  X,
  Archive,
} from "lucide-react";
import axios from "axios";
import { formatClientNameString } from '../../utils/nameFormatter';
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format a YYYY-MM-DD date string for display without timezone conversion
const formatDateStringForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  }
  return new Date(dateString).toLocaleDateString();
};

// Short insurance name — abbreviate common payer names for table columns
const shortInsName = (name) => {
  if (!name) return '';
  const n = name.toUpperCase().trim();
  if (n.includes('BLUE CROSS') && n.includes('BLUE SHIELD')) return 'BCBS';
  if (n.includes('BLUE CROSS')) return 'BC';
  if (n.includes('UNITED HEALTH')) return 'UHC';
  if (n.includes('HARVARD PILGRIM')) return 'HPHC';
  if (n.includes('MASS HEALTH') || n.includes('MASSHEALTH')) return 'MassHealth';
  if (n.includes('TUFTS')) return 'Tufts';
  if (n.includes('AETNA')) return 'Aetna';
  if (n.includes('CIGNA')) return 'Cigna';
  if (n.includes('HUMANA')) return 'Humana';
  if (n.includes('KAISER')) return 'Kaiser';
  if (n.includes('ANTHEM')) return 'Anthem';
  if (n.includes('MEDICAID')) return 'Medicaid';
  if (n.includes('MEDICARE')) return 'Medicare';
  if (n.includes('TRICARE')) return 'Tricare';
  if (n.includes('SELF PAY') || n.includes('SELF-PAY')) return 'Self Pay';
  // Fallback: title-case first two words, max 15 chars
  const words = name.split(/\s+/).slice(0, 2).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  return words.length > 15 ? words.slice(0, 14) + '…' : words;
};

// Helper to get client insurance display — returns "Self Pay" if no insurance
const getClientInsuranceDisplay = (client) => {
  if (!client) return { name: 'Self Pay', short: 'Self Pay', isSelfPay: true };
  
  // Check insurances array first
  const ins = client.insurances?.find(i => i.priority === 1) || client.insurances?.[0];
  if (ins?.company) {
    return { name: ins.company, short: shortInsName(ins.company), isSelfPay: false };
  }
  
  // Fall back to legacy primary_insurance field
  const legacyIns = client.primary_insurance;
  if (legacyIns && legacyIns !== 'NONE' && legacyIns !== 'None' && legacyIns !== '' && legacyIns.toLowerCase() !== 'self pay' && legacyIns.toLowerCase() !== 'self-pay') {
    return { name: legacyIns, short: shortInsName(legacyIns), isSelfPay: false };
  }
  
  // No insurance → Self Pay
  return { name: 'Self Pay', short: 'Self Pay', isSelfPay: true };
};

// Helper to get client display name (handles encrypted/missing fields)
const getClientDisplayName = (client) => {
  if (!client) return 'Unknown Client';
  
  // Try first_name + last_name first
  if (client.first_name && client.last_name) {
    return `${client.first_name} ${client.last_name}`;
  }
  
  // Fall back to full_name
  if (client.full_name) {
    return client.full_name;
  }
  
  // Fall back to preferred_name
  if (client.preferred_name) {
    return client.preferred_name;
  }
  
  // Last resort - return partial name or Unknown
  if (client.first_name) return client.first_name;
  if (client.last_name) return client.last_name;
  
  return 'Unknown Client';
};

/**
 * Multi-select dropdown for providers using Popover + Checkboxes.
 * The generic `MultiSelectFilter` was extracted to
 * `components/shared/MultiSelectFilter.jsx` so other tabs (Eligibility
 * in particular) can reuse the same picker without copy-pasting it.
 */

// Thin wrapper kept for backwards compat with existing callsite
const ProviderMultiSelect = ({ providers, selected, onChange, myAssignedIds = [], mode = 'include', onModeChange }) => {
  const items = React.useMemo(() => providers.map(p => ({
    id: p.id,
    label: `${p.full_name}${p.credentials ? ` (${p.credentials})` : ''}`,
  })), [providers]);
  return (
    <MultiSelectFilter
      items={items}
      selected={selected}
      onChange={onChange}
      mode={mode}
      onModeChange={onModeChange}
      entity="provider"
      triggerTestId="provider-multi-select-trigger"
      shortcutLabel={myAssignedIds.length > 0 ? `My Assigned Providers (${myAssignedIds.length})` : undefined}
      shortcutIds={myAssignedIds}
      popoverWidth={300}
    />
  );
};

export const BillingTab = ({
  // Data
  users,
  providers,
  clients,
  insurances,
  notesReadyForBilling,
  notesSentForCorrection,
  claimsQueue,
  currentBatch,
  user,
  // Loading states
  loadingNotes,
  loadingClaims,
  // Filter states
  billingProviderFilter,
  setBillingProviderFilter,
  billingProviderFilterMode,
  setBillingProviderFilterMode,
  billingClientFilter,
  setBillingClientFilter,
  billingClientFilterMode,
  setBillingClientFilterMode,
  billingInsuranceFilter,
  setBillingInsuranceFilter,
  billingInsuranceFilterMode,
  setBillingInsuranceFilterMode,
  billingCptFilter,
  setBillingCptFilter,
  billingCptFilterMode,
  setBillingCptFilterMode,
  billingDateFrom,
  setBillingDateFrom,
  billingDateTo,
  setBillingDateTo,
  billingSearchTerm,
  setBillingSearchTerm,
  // Actions
  fetchClaimsQueue,
  fetchCurrentBatch,
  fetchNotesReadyForBilling,
  fetchNotesSentForCorrection,
  fetchNoteForReview,
  fetchClaimDetail,
  setCurrentNoteIndex,
  setSelectedNoteForReview,
  setSelectedClaim,
  onViewNote,  // Handler to open note detail dialog
  // State setters for optimistic updates
  setNotesReadyForBilling,
  setNotesSentForCorrection,
  // Mar 2026 — sort state hoisted to App.js so the Prepare Claim
  // dialog's Next/Prev navigation can walk the SAME sorted+filtered
  // list the biller sees in this table.  Bug report: clicking Next
  // Claim used to jump to a random clinician's claim because the
  // dialog walked filter-only Mongo insertion order, ignoring the
  // column-header sort.  Falls back to local state for any caller
  // that hasn't migrated yet.
  sortColumn: sortColumnProp,
  setSortColumn: setSortColumnProp,
  sortDirection: sortDirectionProp,
  setSortDirection: setSortDirectionProp,
}) => {
  // View mode toggle: 'card' or 'list'
  const [viewMode, setViewMode] = useState('list');
  const [billingSortBy, setBillingSortBy] = useState('newest');
  const [nameFormat, setNameFormat] = useState(() => {
    try {
      const v = localStorage.getItem('billingTabNameFormat');
      return v === 'last_first' ? 'last_first' : 'first_last';
    } catch {
      return 'first_last';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('billingTabNameFormat', nameFormat); } catch { /* noop */ }
  }, [nameFormat]);
  const formatClientName = useCallback((name) => {
    if (!name || typeof name !== 'string') return name || 'Unknown Client';
    return formatClientNameString(name, nameFormat);
  }, [nameFormat]);
  
  // Claims needing corrections (fetched locally)
  const [claimsNeedingCorrection, setClaimsNeedingCorrection] = useState([]);
  const [loadingCorrections, setLoadingCorrections] = useState(false);

  // Sorting state for notes-ready-for-billing list view.  Prefer the
  // hoisted state from App.js; fall back to local state when the
  // component is rendered standalone (e.g., tests, stories).
  const [sortColumnLocal, setSortColumnLocal] = useState(null);
  const [sortDirectionLocal, setSortDirectionLocal] = useState('asc');
  const sortColumn = sortColumnProp !== undefined ? sortColumnProp : sortColumnLocal;
  const setSortColumn = setSortColumnProp || setSortColumnLocal;
  const sortDirection = sortDirectionProp !== undefined ? sortDirectionProp : sortDirectionLocal;
  const setSortDirection = setSortDirectionProp || setSortDirectionLocal;

  // Mar 2026 — separate sort state for the Notes Sent Back for
  // Corrections section.  Independent from the ready-for-billing
  // sort so the two tables can be sorted on different columns
  // simultaneously.  Local-only — no need to hoist.
  const [sentBackSortColumn, setSentBackSortColumn] = useState(null);
  const [sentBackSortDirection, setSentBackSortDirection] = useState('asc');
  const toggleSentBackSort = (column) => {
    if (sentBackSortColumn === column) {
      setSentBackSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSentBackSortColumn(column);
      setSentBackSortDirection('asc');
    }
  };
  const SentBackSortIndicator = ({ column }) => {
    if (sentBackSortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sentBackSortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-orange-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-orange-600" />;
  };

  const toggleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIndicator = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-teal-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-teal-600" />;
  };

  // Correction claim editing state — removed custom dialog, now uses Prepare Claim dialog via fetchNoteForReview

  // Collapse/expand state for sections
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    notesReady: false,
    claimsCorrections: false,
    notesSentBack: false,
  });
  const toggleSection = (key) => setSectionsCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // -------------------------------------------------------------------
  // Legacy Billing Transition — multi-select + bulk-mark-billed-in-legacy
  // -------------------------------------------------------------------
  const [selectedNoteIds, setSelectedNoteIds] = useState(new Set());
  const [showLegacyMarkDialog, setShowLegacyMarkDialog] = useState(false);
  const [legacyMarkInProgress, setLegacyMarkInProgress] = useState(false);
  const [legacyRefNumber, setLegacyRefNumber] = useState('');
  const [legacySystemName, setLegacySystemName] = useState('');
  const [legacySubmissionDate, setLegacySubmissionDate] = useState('');
  const [legacyDefaultCptCode, setLegacyDefaultCptCode] = useState('');
  const [legacyDefaultCharge, setLegacyDefaultCharge] = useState('');

  const toggleSelectNote = (noteId, checked) => {
    setSelectedNoteIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(noteId); else next.delete(noteId);
      return next;
    });
  };
  const toggleSelectAll = (noteIds, checked) => {
    setSelectedNoteIds(prev => {
      const next = new Set(prev);
      if (checked) noteIds.forEach(id => next.add(id));
      else noteIds.forEach(id => next.delete(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedNoteIds(new Set());

  const submitLegacyBulkMark = async () => {
    if (selectedNoteIds.size === 0) return;
    setLegacyMarkInProgress(true);
    try {
      const payload = {
        note_ids: Array.from(selectedNoteIds),
        legacy_reference_number: legacyRefNumber.trim() || null,
        legacy_system_name: legacySystemName.trim() || null,
        legacy_submission_date: legacySubmissionDate || null,
        default_cpt_code: legacyDefaultCptCode.trim() || null,
        default_charge_amount: legacyDefaultCharge ? parseFloat(legacyDefaultCharge) : null,
      };
      const resp = await axios.post(`${API}/billing/notes/bulk-mark-legacy-billed`, payload);
      const { summary, skipped, errors } = resp.data || {};
      if (summary?.created > 0) {
        toast.success(`Created ${summary.created} legacy stub claim${summary.created > 1 ? 's' : ''}` +
          (summary.skipped ? ` · ${summary.skipped} skipped` : '') +
          (summary.errors ? ` · ${summary.errors} error${summary.errors > 1 ? 's' : ''}` : ''));
      } else if (summary?.skipped > 0 && summary?.created === 0) {
        toast.warning(`All ${summary.skipped} note${summary.skipped > 1 ? 's were' : ' was'} skipped (claim already exists)`);
      } else if (summary?.errors > 0) {
        toast.error(`Failed to create stubs: ${summary.errors} error${summary.errors > 1 ? 's' : ''}`);
      }
      // Show specific errors in a second toast so the user knows WHY it failed
      if (errors && errors.length > 0) {
        console.error('[legacy bulk-mark] errors:', errors);
        const firstErr = errors[0]?.error || 'Unknown error';
        toast.error(
          errors.length === 1
            ? firstErr
            : `${firstErr} (and ${errors.length - 1} more — see console)`,
          { duration: 8000 }
        );
      }
      if (skipped && skipped.length > 0) {
        console.log('[legacy bulk-mark] skipped:', skipped);
      }
      // Optimistically remove successfully-marked notes from the billing list
      const createdIds = new Set((resp.data?.created || []).map(c => c.note_id));
      if (createdIds.size > 0 && typeof setNotesReadyForBilling === 'function') {
        setNotesReadyForBilling(prev => prev.filter(n => !createdIds.has(n.id)));
      }
      // Also fire a global event so the claims queue / stats panels can refresh
      if (createdIds.size > 0) {
        window.dispatchEvent(new CustomEvent('billingTabNotesUpdated', { detail: { removedIds: Array.from(createdIds) } }));
      }
      setShowLegacyMarkDialog(false);
      setLegacyRefNumber('');
      setLegacySystemName('');
      setLegacySubmissionDate('');
      setLegacyDefaultCptCode('');
      setLegacyDefaultCharge('');
      clearSelection();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark notes as legacy-billed');
    } finally {
      setLegacyMarkInProgress(false);
    }
  };

  const fetchClaimsNeedingCorrection = useCallback(async () => {
    setLoadingCorrections(true);
    try {
      const response = await axios.get(`${API}/claims/queue?status=needs_correction`);
      setClaimsNeedingCorrection(response.data?.claims || []);
    } catch (error) {
      console.error("Failed to fetch claims needing correction:", error);
    } finally {
      setLoadingCorrections(false);
    }
  }, []);

  useEffect(() => {
    fetchClaimsNeedingCorrection();
  }, [fetchClaimsNeedingCorrection]);
  
  // Ensure arrays have default values to prevent .filter() errors
  const safeClaimsQueue = Array.isArray(claimsQueue) ? claimsQueue : [];
  const safeNotesReadyForBilling = Array.isArray(notesReadyForBilling) ? notesReadyForBilling : [];
  const safeNotesSentForCorrection = Array.isArray(notesSentForCorrection) ? notesSentForCorrection : [];

  // Provider filter predicate honoring include/exclude mode. Empty filter = show all.
  const providerMatches = React.useCallback((providerId) => {
    if (!Array.isArray(billingProviderFilter) || billingProviderFilter.length === 0) return true;
    const inList = billingProviderFilter.includes(providerId);
    return billingProviderFilterMode === 'exclude' ? !inList : inList;
  }, [billingProviderFilter, billingProviderFilterMode]);

  // Client filter predicate — multi-select with include/exclude. Empty = show all.
  const clientMatches = React.useCallback((clientId) => {
    const arr = Array.isArray(billingClientFilter)
      ? billingClientFilter
      : (billingClientFilter && billingClientFilter !== 'all' ? [billingClientFilter] : []);
    if (arr.length === 0) return true;
    const inList = arr.includes(clientId);
    return billingClientFilterMode === 'exclude' ? !inList : inList;
  }, [billingClientFilter, billingClientFilterMode]);

  // Insurance multi-select with include/exclude mode.  ``billingInsuranceFilter``
  // is an array of insurance NAMES (with the special token 'Self Pay').
  // Empty array = no filter.  Values are matched case-insensitively as
  // substrings against the note/claim's insurance name (mirrors the
  // legacy single-string behaviour).
  const insuranceMatches = React.useCallback((rawIns) => {
    const arr = Array.isArray(billingInsuranceFilter)
      ? billingInsuranceFilter
      : (billingInsuranceFilter && billingInsuranceFilter !== 'all'
          ? [billingInsuranceFilter] : []);
    if (arr.length === 0) return true;
    const ins = (rawIns || '').toLowerCase();
    const inList = arr.some((sel) => {
      if (sel === 'Self Pay') {
        // Self Pay matches when the row has no real insurance OR
        // explicitly stores 'Self Pay'.
        return !rawIns || rawIns === 'Self Pay';
      }
      return ins.includes(sel.toLowerCase());
    });
    return billingInsuranceFilterMode === 'exclude' ? !inList : inList;
  }, [billingInsuranceFilter, billingInsuranceFilterMode]);

  // CPT multi-select with include/exclude mode (Feb 2026).  Accepts
  // either a single CPT string (notes: ``_billing.cpt_code``) OR an
  // array (claims: ``service_lines[].cpt_code``).  Empty filter =
  // pass-through.  Matching is exact string comparison.
  //
  // Include mode: row passes if ANY of its CPT codes is in the
  //   selected set (mirrors how a biller thinks: "show me 90837s").
  // Exclude mode: row passes if NONE of its CPT codes is in the
  //   selected set (mirrors: "hide 90791 intake notes").
  const cptMatches = React.useCallback((cptValue) => {
    const arr = Array.isArray(billingCptFilter)
      ? billingCptFilter
      : (billingCptFilter && billingCptFilter !== 'all' ? [billingCptFilter] : []);
    if (arr.length === 0) return true;
    const rowCpts = Array.isArray(cptValue)
      ? cptValue.filter(Boolean).map(String)
      : (cptValue ? [String(cptValue)] : []);
    const selSet = new Set(arr.map(String));
    const anyInList = rowCpts.some((c) => selSet.has(c));
    return billingCptFilterMode === 'exclude' ? !anyInList : anyInList;
  }, [billingCptFilter, billingCptFilterMode]);

  // Apply filters to notes
  const getFilteredNotes = () => {
    return safeNotesReadyForBilling.filter(note => {
      if (!providerMatches(note.provider_id)) return false;
      if (!clientMatches(note.client_id)) return false;
      if (!billingSearchMatches(note._client_name || note.client_name, note.provider_name, note._billing?.cpt_code)) return false;
      
      // Insurance filter — multi-select with include/exclude mode.
      if (!insuranceMatches(note._client_insurance)) return false;

      // CPT filter — multi-select with include/exclude mode.
      if (!cptMatches(note._billing?.cpt_code)) return false;
      
      // Date range filter
      if (billingDateFrom && note.date_of_service < billingDateFrom) return false;
      if (billingDateTo && note.date_of_service > billingDateTo) return false;
      
      return true;
    });
  };

  // Jun 2026 — quick text search above the dropdowns (client / provider /
  // CPT), matching the search-first layout on the other tabs.
  const [billingTabSearch, setBillingTabSearch] = React.useState('');
  const [billingFiltersCollapsed, setBillingFiltersCollapsed] = React.useState(() => {
    try { return localStorage.getItem('billing_filters_collapsed') === '1'; } catch { return false; }
  });
  const toggleBillingFiltersCollapsed = () => setBillingFiltersCollapsed((prev) => {
    const next = !prev;
    try { localStorage.setItem('billing_filters_collapsed', next ? '1' : '0'); } catch { /* noop */ }
    return next;
  });
  const billingSearchMatches = React.useCallback((clientName, providerName, cpt) => {
    const q = billingTabSearch.trim().toLowerCase();
    if (!q) return true;
    return [clientName, providerName, cpt].some(
      (v) => (v || '').toString().toLowerCase().includes(q)
    );
  }, [billingTabSearch]);

  const filteredNotes = getFilteredNotes();

  // Apply the same filters to Claims Needing Corrections
  const filteredClaimsCorrections = React.useMemo(() => {
    return claimsNeedingCorrection.filter(claim => {
      if (!providerMatches(claim.provider_id)) return false;
      if (!clientMatches(claim.client_id)) return false;
      if (!insuranceMatches(claim.is_self_pay ? 'Self Pay' : claim.primary_insurance)) return false;
      if (!billingSearchMatches(claim.client_name, claim.provider_name, (claim.service_lines || []).map(sl => sl.cpt_code).join(','))) return false;
      // Claims carry multiple CPTs via service_lines; pass the array.
      if (!cptMatches((claim.service_lines || []).map(sl => sl.cpt_code))) return false;
      if (billingDateFrom && claim.date_of_service < billingDateFrom) return false;
      if (billingDateTo && claim.date_of_service > billingDateTo) return false;
      return true;
    });
  }, [claimsNeedingCorrection, providerMatches, clientMatches, insuranceMatches, cptMatches, billingDateFrom, billingDateTo]);

  // Apply the same filters to Notes Sent Back for Corrections
  const filteredNotesSentBack = React.useMemo(() => {
    return safeNotesSentForCorrection.filter(note => {
      if (!providerMatches(note.provider_id)) return false;
      if (!clientMatches(note.client_id)) return false;
      if (!insuranceMatches(note._client_insurance)) return false;
      if (!cptMatches(note._billing?.cpt_code)) return false;
      if (billingDateFrom && note.date_of_service < billingDateFrom) return false;
      if (billingDateTo && note.date_of_service > billingDateTo) return false;
      return true;
    });
  }, [safeNotesSentForCorrection, providerMatches, clientMatches, insuranceMatches, cptMatches, billingDateFrom, billingDateTo]);

  // Apply sorting to the filtered notes
  const sortedNotes = React.useMemo(() => {
    if (!sortColumn) return filteredNotes;
    return [...filteredNotes].sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'client':
          aVal = a._client_name || ''; bVal = b._client_name || ''; break;
        case 'mrn':
          aVal = a._client_mrn || ''; bVal = b._client_mrn || ''; break;
        case 'insurance':
          aVal = a._client_insurance || ''; bVal = b._client_insurance || ''; break;
        case 'provider':
          aVal = a._provider_name || ''; bVal = b._provider_name || ''; break;
        case 'date':
          aVal = a.date_of_service || ''; bVal = b.date_of_service || ''; break;
        case 'deadline': {
          // Sort by days-until-deadline (ascending = most-urgent-first by
          // default). We approximate the deadline as DOS + 90 days — same
          // assumption as BillingDeadlineBadge. Notes missing a DOS sink to
          // the end so they don't pollute the "urgent" top.
          const calc = (note) => {
            const iso = note.date_of_service;
            if (!iso) return Number.POSITIVE_INFINITY;
            const s = String(iso).slice(0, 10);
            const [y, m, d] = s.split('-').map(Number);
            if (!y || !m || !d) return Number.POSITIVE_INFINITY;
            const dos = new Date(y, m - 1, d);
            const deadline = new Date(dos.getTime() + 90 * 86400000);
            const today = new Date(); today.setHours(0, 0, 0, 0);
            return Math.floor((deadline - today) / 86400000);
          };
          aVal = calc(a); bVal = calc(b); break;
        }
        case 'cpt':
          aVal = a._billing?.cpt_code || ''; bVal = b._billing?.cpt_code || ''; break;
        case 'charges':
          aVal = Number(a._billing?.charges) || 0; bVal = Number(b._billing?.charges) || 0; break;
        default: return 0;
      }
      if (typeof aVal === 'number') return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      return sortDirection === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredNotes, sortColumn, sortDirection]);

  // Mar 2026 — sorted Notes Sent Back for Corrections list.  Mirrors
  // the `sortedNotes` pattern above but with column keys matching the
  // sent-back table headers.  Falls through to insertion order when
  // no column is selected.
  const sortedNotesSentBack = React.useMemo(() => {
    if (!sentBackSortColumn) return filteredNotesSentBack;
    return [...filteredNotesSentBack].sort((a, b) => {
      let aVal, bVal;
      switch (sentBackSortColumn) {
        case 'client':
          aVal = a._client_name || a.client_name || ''; bVal = b._client_name || b.client_name || ''; break;
        case 'insurance':
          aVal = a._client_insurance || ''; bVal = b._client_insurance || ''; break;
        case 'provider':
          aVal = a._provider_name
            || users.find(u => u.id === a.provider_id)?.full_name
            || providers.find(p => p.id === a.provider_id)?.full_name
            || '';
          bVal = b._provider_name
            || users.find(u => u.id === b.provider_id)?.full_name
            || providers.find(p => p.id === b.provider_id)?.full_name
            || '';
          break;
        case 'dos':
          aVal = a.date_of_service || ''; bVal = b.date_of_service || ''; break;
        case 'note':
          aVal = a.title || a.template_name || a.form_name || '';
          bVal = b.title || b.template_name || b.form_name || '';
          break;
        case 'reason':
          aVal = a.needs_corrections_reason || a.biller_notes || '';
          bVal = b.needs_corrections_reason || b.biller_notes || '';
          break;
        case 'sent_back':
          // Sort by the latest update timestamp (when the biller sent
          // it back).  ISO strings are lexically comparable.
          aVal = a.updated_at || ''; bVal = b.updated_at || ''; break;
        default: return 0;
      }
      if (typeof aVal === 'number') return sentBackSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      return sentBackSortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredNotesSentBack, sentBackSortColumn, sentBackSortDirection, users, providers]);

  // Pagination hooks for all three sections
  const notesPagination = usePagination(sortedNotes, 25);
  const correctionsPagination = usePagination(filteredClaimsCorrections, 25);
  const sentBackPagination = usePagination(sortedNotesSentBack, 25);

  // Move note to "Claims Needing Corrections" — creates a claim draft then marks it needs_correction
  const [movingToCorrection, setMovingToCorrection] = useState(null);
  const [sendingBackNote, setSendingBackNote] = useState(null);

  const handleSendNoteBackForCorrections = async (note) => {
    const reason = window.prompt('Reason for sending back to provider:');
    if (!reason) return;
    setSendingBackNote(note.id);
    try {
      await axios.put(`${API}/notes/${note.id}`, {
        status: 'needs_corrections',
        needs_corrections_reason: reason,
        biller_notes: reason,
      });
      toast.success('Note sent back to provider for corrections');
      // Optimistic update
      setNotesReadyForBilling(prev => prev.filter(n => n.id !== note.id));
      setNotesSentForCorrection(prev => {
        if (prev.some(n => n.id === note.id)) return prev;
        return [...prev, { ...note, status: 'needs_corrections', needs_corrections_reason: reason }];
      });
    } catch (error) {
      console.error('Failed to send note back:', error);
      toast.error(error.response?.data?.detail || 'Failed to send note back');
    } finally {
      setSendingBackNote(null);
    }
  };

  const handleMoveToCorrections = async (note) => {
    const reason = window.prompt('Correction reason (e.g., "Updating insurance info"):');
    if (!reason) return;
    setMovingToCorrection(note.id);
    try {
      // Step 1: Create claim from note
      const createResp = await axios.post(`${API}/claims/from-note/${note.id}`);
      const claimId = createResp.data?.claim_id || createResp.data?.id;
      if (!claimId) throw new Error('No claim ID returned');
      // Step 2: Return it for corrections
      await axios.post(`${API}/claims/${claimId}/return-for-corrections`, { notes: reason });
      toast.success('Claim created and moved to Needs Corrections');
      // Optimistic update
      setNotesReadyForBilling(prev => prev.filter(n => n.id !== note.id));
      fetchClaimsNeedingCorrection();
    } catch (error) {
      console.error('Failed to move to corrections:', error);
      toast.error(error.response?.data?.detail || 'Failed to move to corrections');
    } finally {
      setMovingToCorrection(null);
    }
  };

  const handleRefresh = () => {
    fetchClaimsQueue();
    fetchCurrentBatch();
    fetchNotesReadyForBilling();
    fetchNotesSentForCorrection();
    fetchClaimsNeedingCorrection();
  };

  const handleClearFilters = () => {
    setBillingProviderFilter([]);
    // Normalise to an empty array — the MultiSelectFilter consumer
    // works exclusively in array semantics; the legacy 'all' sentinel
    // caused "click clear twice" UX because the popover saw [] while
    // the underlying filter still held 'all'. (May 2026 audit.)
    setBillingClientFilter([]);
    setBillingInsuranceFilter([]);
    setBillingInsuranceFilterMode('include');
    setBillingCptFilter([]);
    setBillingCptFilterMode('include');
    setBillingDateFrom('');
    setBillingDateTo('');
    setBillingSearchTerm('');
  };

  const handleCloseBatch = async () => {
    if (!window.confirm(`Close Batch #${currentBatch.batch_number} with ${currentBatch.total_claims} claims?`)) return;
    try {
      await axios.post(`${API}/batches/${currentBatch.id}/close`);
      toast.success("Batch closed successfully");
      fetchCurrentBatch();
      fetchClaimsQueue();
    } catch (error) {
      console.error("Failed to close batch:", error);
      toast.error(error.response?.data?.detail || "Failed to close batch");
    }
  };

  const handleCloseAndGenerate = async () => {
    if (!window.confirm(`Close Batch #${currentBatch.batch_number} and generate 837P file for ${currentBatch.total_claims} claims?`)) return;
    try {
      const response = await axios.post(`${API}/batches/${currentBatch.id}/close-and-generate`);
      toast.success(`Batch closed! 837P file generated: ${response.data.file_name}`);
      fetchCurrentBatch();
      fetchClaimsQueue();
    } catch (error) {
      console.error("Failed to close and generate:", error);
      toast.error(error.response?.data?.detail || "Failed to generate 837P file");
    }
  };

  const handleSubmitAllToBatch = async () => {
    const readyClaims = safeClaimsQueue.filter(c => c.status === 'ready_for_billing');
    if (!window.confirm(`Submit all ${readyClaims.length} ready claims to batch?`)) return;
    
    try {
      const promises = readyClaims.map(claim => 
        axios.post(`${API}/claims/${claim.id}/add-to-batch`)
      );
      await Promise.all(promises);
      
      toast.success(`${readyClaims.length} claims submitted to batch successfully`);
      fetchClaimsQueue();
      fetchCurrentBatch();
    } catch (error) {
      console.error("Failed to batch submit:", error);
      toast.error("Failed to submit some claims to batch");
    }
  };

  const handleSubmitClaimToBatch = async (claim) => {
    if (!window.confirm(`Submit claim for ${claim.client_name} to billing batch?`)) return;
    try {
      await axios.post(`${API}/claims/${claim.id}/add-to-batch`);
      toast.success("Claim submitted to batch successfully");
      fetchClaimsQueue();
      fetchCurrentBatch();
    } catch (error) {
      console.error("Failed to submit claim:", error);
      toast.error(error.response?.data?.detail || "Failed to submit claim");
    }
  };

  // Claim Viewer state
  const [viewingClaimId, setViewingClaimId] = useState(null);
  const [viewingClaimName, setViewingClaimName] = useState('');

  const handleViewClaim = (claim) => {
    setViewingClaimId(claim.id);
    setViewingClaimName(claim.client_name || 'Claim');
  };

  const userRoleName = useUserRoleName();
  const canManageBatch = user?.is_billing_manager || userRoleName === 'admin' || userRoleName === 'super admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Receipt className="h-5 w-5 text-teal-600" />Billing</h2>
          <p className="text-sm text-gray-500">Process claims and submit to batches</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="icon"
            title="Refresh billing data"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
            data-testid="billing-refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <DisabledHeaderDots testId="billing-tools-menu-btn" />
        </div>
      </div>

      {/* Filters — "Filters" heading removed Jun 2026 (user request);
          the aging-date reference panel stays. */}
      <Card>
        <CardContent className="pt-4 flex flex-col">
          {/* Search — top of the box, matching every other tab (Jun 2026). */}
          <div className="order-first mb-4 flex items-center gap-2" data-testid="billing-search-row">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search client, provider, or CPT..."
                value={billingTabSearch}
                onChange={(e) => setBillingTabSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="billing-search-input"
              />
            </div>
            <button
              onClick={toggleBillingFiltersCollapsed}
              className="h-9 px-3 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5 text-sm font-medium shrink-0"
              data-testid="billing-filters-collapse-toggle"
              title={billingFiltersCollapsed ? 'Show filters' : 'Hide filters'}
            >
              <Filter className="h-4 w-4" />
              {billingFiltersCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>
          {/* Aging-date reference — single horizontal line, shown BELOW
              the filter grid via flex ordering (Jun 2026 user request). */}
          {!billingFiltersCollapsed && (() => {
            const fmt = (d) => d.toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            });
            const now = new Date();
            const daysAgo = (n) => {
              const d = new Date(now);
              d.setDate(d.getDate() - n);
              return d;
            };
            const yearsAgo = (n) => {
              const d = new Date(now);
              d.setFullYear(d.getFullYear() - n);
              return d;
            };
            return (
              <div
                className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-3 order-3 flex flex-wrap items-center gap-x-4 gap-y-1"
                data-testid="billing-aging-dates-ref"
                title="Reference dates for common AR aging buckets — copy these into Date From / Date To manually."
              >
                <span className="font-medium text-amber-900">For reference:</span>
                <span className="text-gray-600">90 days ago: <span className="font-mono text-gray-900">{fmt(daysAgo(90))}</span></span>
                <span className="text-gray-600">120 days ago: <span className="font-mono text-gray-900">{fmt(daysAgo(120))}</span></span>
                <span className="text-gray-600">1 year ago: <span className="font-mono text-gray-900">{fmt(yearsAgo(1))}</span></span>
              </div>
            );
          })()}
          {!billingFiltersCollapsed && (
          <div className="order-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 pt-3">
            <div>
              <Label>Provider</Label>
              <ProviderMultiSelect
                providers={[
                  ...users.filter(u => u.is_provider),
                  ...providers.filter(p => !users.some(u => u.id === p.id))
                ].sort((a, b) => (a.first_name || a.full_name || '').localeCompare(b.first_name || b.full_name || ''))}
                selected={billingProviderFilter}
                onChange={setBillingProviderFilter}
                mode={billingProviderFilterMode}
                onModeChange={setBillingProviderFilterMode}
                myAssignedIds={
                  user?.is_biller
                    ? users.filter(u => (u.assigned_billers || []).includes(user.id)).map(u => u.id)
                    : []
                }
              />
            </div>
            
            <div>
              <Label>Client</Label>
              {(() => {
                // Build the dropdown list. Primary source is the `clients`
                // prop (= allClientsForLookup), but we also union in any
                // client_id referenced by a CLAIM that isn't in the list.
                // This is the safety net for the production case where a
                // claim exists for a client whose lookup-list entry hasn't
                // arrived yet (background pagination still running, or a
                // soft-archived client the lookup query filtered out).
                const lookupById = new Map(
                  (clients || []).map(c => [c.id, c])
                );
                // Union claims-referenced clients into the map.
                // We use `claimsNeedingCorrection` (the only claims state in
                // this component); a previous edit referenced a bare `claims`
                // variable that doesn't exist, which threw ReferenceError on
                // first render of the dropdown.
                for (const claim of (claimsNeedingCorrection || [])) {
                  if (!claim.client_id || lookupById.has(claim.client_id)) continue;
                  // Build a minimal stub from the claim's stored client_name.
                  const rawName = claim.client_name || '';
                  const parts = rawName.split(',').map(s => s.trim());
                  const last_name = parts[0] || '';
                  const first_name = parts[1] || '';
                  lookupById.set(claim.client_id, {
                    id: claim.client_id,
                    last_name,
                    first_name,
                    full_name: rawName || 'Unknown Client',
                    status: 'active',
                    _from_claim: true,
                  });
                }

                const items = Array.from(lookupById.values())
                  .sort((a, b) => {
                    const aActive = (a.status || 'active') === 'active';
                    const bActive = (b.status || 'active') === 'active';
                    if (aActive !== bActive) return aActive ? -1 : 1;
                    const lnA = (a.last_name || a.full_name || '').toLowerCase();
                    const lnB = (b.last_name || b.full_name || '').toLowerCase();
                    if (lnA !== lnB) return lnA.localeCompare(lnB);
                    return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
                  })
                  .map(c => {
                    const label = `${c.last_name || ''}, ${c.first_name || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || c.full_name || 'Unknown';
                    const isInactive = c.status && c.status !== 'active';
                    return {
                      id: c.id,
                      label,
                      sublabel: [c.mrn, isInactive ? c.status : null].filter(Boolean).join(' · '),
                    };
                  });
                const selectedArr = Array.isArray(billingClientFilter)
                  ? billingClientFilter
                  : (billingClientFilter && billingClientFilter !== 'all' ? [billingClientFilter] : []);
                return (
                  <MultiSelectFilter
                    items={items}
                    selected={selectedArr}
                    onChange={setBillingClientFilter}
                    mode={billingClientFilterMode}
                    onModeChange={setBillingClientFilterMode}
                    entity="client"
                    triggerTestId="billing-client-filter-trigger"
                    popoverWidth={340}
                  />
                );
              })()}
            </div>
            
            <div>
              <Label>Insurance</Label>
              {(() => {
                // Build the dropdown items from the prop list of insurances,
                // plus a synthetic "Self Pay" entry up top.  Matching is
                // by NAME (not id) because the filter logic compares to
                // the note/claim's enriched insurance string.
                const items = [
                  { id: 'Self Pay', label: 'Self Pay' },
                  ...[...(insurances || [])]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(ins => ({ id: ins.name, label: ins.name })),
                ];
                const selectedArr = Array.isArray(billingInsuranceFilter)
                  ? billingInsuranceFilter
                  : (billingInsuranceFilter && billingInsuranceFilter !== 'all'
                      ? [billingInsuranceFilter] : []);
                return (
                  <MultiSelectFilter
                    items={items}
                    selected={selectedArr}
                    onChange={setBillingInsuranceFilter}
                    mode={billingInsuranceFilterMode}
                    onModeChange={setBillingInsuranceFilterMode}
                    entity="insurance"
                    triggerTestId="billing-insurance-filter-trigger"
                    popoverWidth={340}
                  />
                );
              })()}
            </div>

            <div>
              <Label>CPT</Label>
              {(() => {
                // Build the CPT dropdown from the UNION of CPT codes
                // currently visible in any of the three billing
                // surfaces (notes ready, notes sent back, claims
                // needing corrections + queue).  This keeps the
                // dropdown tight — only CPTs that actually exist on
                // the screen — rather than spamming the full CPT
                // catalogue.  Empty placeholder = no filter.
                const cptSet = new Set();
                for (const n of (safeNotesReadyForBilling || [])) {
                  const c = n?._billing?.cpt_code;
                  if (c) cptSet.add(String(c));
                }
                for (const n of (safeNotesSentForCorrection || [])) {
                  const c = n?._billing?.cpt_code;
                  if (c) cptSet.add(String(c));
                }
                for (const cl of (claimsNeedingCorrection || [])) {
                  for (const sl of (cl?.service_lines || [])) {
                    if (sl?.cpt_code) cptSet.add(String(sl.cpt_code));
                  }
                }
                for (const cl of (safeClaimsQueue || [])) {
                  for (const sl of (cl?.service_lines || [])) {
                    if (sl?.cpt_code) cptSet.add(String(sl.cpt_code));
                  }
                }
                const items = Array.from(cptSet)
                  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                  .map(code => ({ id: code, label: code }));
                const selectedArr = Array.isArray(billingCptFilter)
                  ? billingCptFilter
                  : (billingCptFilter && billingCptFilter !== 'all'
                      ? [billingCptFilter] : []);
                return (
                  <MultiSelectFilter
                    items={items}
                    selected={selectedArr}
                    onChange={setBillingCptFilter}
                    mode={billingCptFilterMode}
                    onModeChange={setBillingCptFilterMode}
                    entity="CPT"
                    triggerTestId="billing-cpt-filter-trigger"
                    popoverWidth={260}
                  />
                );
              })()}
            </div>
            
            <div>
              <Label>Date From</Label>
              <DateField
                value={billingDateFrom}
                onChange={setBillingDateFrom}
                testId="billing-date-from"
              />
            </div>
            
            <div>
              <Label>Date To</Label>
              <DateField
                value={billingDateTo}
                onChange={setBillingDateTo}
                testId="billing-date-to"
              />
            </div>
            {/* Clear — same row as the dropdowns/date pickers (Jun 2026),
                styled like the Claims Workshop Clear Filters button. */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium whitespace-nowrap"
                data-testid="billing-clear-filters-btn"
              >
                Clear Filters
              </button>
            </div>
          </div>
          )}

          {/* Sort row — directly under the search bar (Jun 2026). */}
          <div className="order-1 flex flex-wrap items-center gap-3" data-testid="billing-sort-row">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Sort:</span>
              <select
                className="text-sm border rounded px-2 py-1"
                value={billingSortBy}
                onChange={(e) => {
                  const v = e.target.value;
                  setBillingSortBy(v);
                  const dir = v === 'oldest' ? 'asc' : 'desc';
                  setSortColumn('date');
                  setSortDirection(dir);
                  setSentBackSortColumn('dos');
                  setSentBackSortDirection(dir);
                }}
                data-testid="billing-sort-select"
              >
                <option value="oldest">Oldest First</option>
                <option value="newest">Newest First</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Per page:</span>
              <select
                className="text-sm border rounded px-2 py-1"
                value={notesPagination.pageSize}
                onChange={(e) => {
                  const n = parseInt(e.target.value);
                  notesPagination.setPageSize(n);
                  notesPagination.setPage(1);
                  correctionsPagination.setPageSize(n);
                  correctionsPagination.setPage(1);
                  sentBackPagination.setPageSize(n);
                  sentBackPagination.setPage(1);
                }}
                data-testid="billing-per-page-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex border rounded-md" data-testid="billing-view-mode-toggle">
              <Button
                variant={viewMode === 'card' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-r-none h-8"
                onClick={() => setViewMode('card')}
                title="Card view"
                data-testid="billing-view-card-btn"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-l-none h-8"
                onClick={() => setViewMode('list')}
                title="List view"
                data-testid="billing-view-list-btn"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => setNameFormat(nameFormat === 'first_last' ? 'last_first' : 'first_last')}
              title={nameFormat === 'first_last' ? 'Switch to LAST, First' : 'Switch to First LAST'}
              data-testid="billing-name-format-toggle"
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
              {nameFormat === 'first_last' ? 'First LAST' : 'LAST, First'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Phase 4 Ledger Work Queues (Reversals & Unapplied Payer Credits)
          are hidden until the billing team requests them. Imports kept so we
          can re-enable by uncommenting a single block below. */}
      {false && (
        <>
          <ReversalWorkQueueCard
            onOpenClaim={(claimId) => {
              if (typeof fetchClaimDetail === 'function') fetchClaimDetail(claimId);
            }}
          />
          <UnappliedPayerCreditsCard />
        </>
      )}

      {/* Notes Ready for Billing */}
      <Card className="border-l-4 border-l-amber-400">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('notesReady')}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ChevronDown className={`h-4 w-4 transition-transform ${sectionsCollapsed.notesReady ? '-rotate-90' : ''}`} />
              Notes Ready for Billing
            </span>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Badge className="bg-amber-400 text-white">
                {loadingNotes ? 'Loading...' : `${filteredNotes.length} notes`}
              </Badge>
            </div>
          </CardTitle>
          <p className="text-sm text-gray-600">Create claims from signed notes</p>
        </CardHeader>
        {!sectionsCollapsed.notesReady && (
        <CardContent>
          {loadingNotes ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">Loading notes...</p>
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No notes match your filters</p>
              <p className="text-sm mt-2">Try adjusting your filter criteria</p>
            </div>
          ) : viewMode === 'list' ? (
            /* List View */
            <div className="overflow-x-auto">
              {/* Bulk-action bar for Legacy Billing Transition */}
              {selectedNoteIds.size > 0 && (
                <div className="mb-3 flex items-center justify-between gap-3 p-3 bg-purple-50 border border-purple-200 rounded-md" data-testid="legacy-bulk-action-bar">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-purple-700" />
                    <span className="font-medium text-purple-900">
                      {selectedNoteIds.size} note{selectedNoteIds.size > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearSelection}
                      data-testid="legacy-bulk-clear-btn"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="bg-purple-700 hover:bg-purple-800 text-white"
                      onClick={() => setShowLegacyMarkDialog(true)}
                      data-testid="legacy-bulk-mark-btn"
                    >
                      <Archive className="h-3.5 w-3.5 mr-1" />
                      Mark as Billed in Legacy System
                    </Button>
                  </div>
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="w-8 p-2">
                      <Checkbox
                        checked={notesPagination.paginatedItems.length > 0
                          && notesPagination.paginatedItems.every(n => selectedNoteIds.has(n.id))}
                        onCheckedChange={(v) => toggleSelectAll(notesPagination.paginatedItems.map(n => n.id), !!v)}
                        data-testid="legacy-select-all"
                        aria-label="Select all notes on this page"
                      />
                    </th>
                    <th className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('client')}>
                      <span className="flex items-center">Client<SortIndicator column="client" /></span>
                    </th>
                    <th className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('insurance')}>
                      <span className="flex items-center">Ins<SortIndicator column="insurance" /></span>
                    </th>
                    <th className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('provider')}>
                      <span className="flex items-center">Provider<SortIndicator column="provider" /></span>
                    </th>
                    <th className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('date')}>
                      <span className="flex items-center">Date<SortIndicator column="date" /></span>
                    </th>
                    <th className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('deadline')} title="Sort by timely-filing deadline (most urgent first)">
                      <span className="flex items-center">Deadline<SortIndicator column="deadline" /></span>
                    </th>
                    <th className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('cpt')}>
                      <span className="flex items-center">CPT<SortIndicator column="cpt" /></span>
                    </th>
                    <th className="text-left p-3 font-medium">Mod</th>
                    <th className="text-left p-3 font-medium">POS</th>
                    <th className="text-center p-3 font-medium">Units</th>
                    <th className="text-right p-3 font-medium cursor-pointer hover:bg-gray-100 select-none" onClick={() => toggleSort('charges')}>
                      <span className="flex items-center justify-end">Charges<SortIndicator column="charges" /></span>
                    </th>
                    <th className="text-center p-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {notesPagination.paginatedItems.map((note, index) => {
                    const noteProvider = note._provider_name || users.find(u => u.id === note.provider_id)?.full_name || providers.find(p => p.id === note.provider_id)?.full_name;
                    const b = note._billing || {};
                    const isSelfPay = note._client_is_self_pay !== false && (!note._client_insurance || note._client_insurance === 'Self Pay');
                    
                    return (
                      <tr key={note.id} className={`border-b hover:bg-yellow-50 ${selectedNoteIds.has(note.id) ? 'bg-purple-50 hover:bg-purple-100' : ''}`}>
                        <td className="w-8 p-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedNoteIds.has(note.id)}
                            onCheckedChange={(v) => toggleSelectNote(note.id, !!v)}
                            data-testid={`legacy-select-note-${note.id}`}
                            aria-label={`Select ${note._client_name || 'note'}`}
                          />
                        </td>
                        <td className="p-3">
                          <div className="text-[10px] text-gray-400 font-mono leading-tight">{note._client_mrn || ''}</div>
                          <div className="font-medium">{formatClientName(note._client_name || note.client_name)}</div>
                        </td>
                        <td className="p-3 text-xs text-gray-700 max-w-[100px] truncate" title={note._client_insurance || 'Self Pay'}>
                          {isSelfPay 
                            ? <span className="text-emerald-700 font-medium">Self Pay</span>
                            : shortInsName(note._client_insurance) || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="p-3">{noteProvider || 'Unknown'}</td>
                        <td className="p-3 text-sm">{formatDateStringForDisplay(note.date_of_service)}</td>
                        <td className="p-3">
                          <BillingDeadlineBadge
                            dos={note.date_of_service}
                            payerName={isSelfPay ? null : (note._client_insurance || null)}
                          />
                        </td>
                        <td className="p-3">
                          {b.cpt_code ? (
                            <span className="font-mono text-sm font-semibold text-gray-900">{b.cpt_code}</span>
                          ) : (
                            <span className="text-xs text-amber-600 italic">No CPT</span>
                          )}
                        </td>
                        <td className="p-3">
                          {b.modifiers?.length > 0 && (
                            <span className="font-mono text-xs text-gray-700">{b.modifiers.join(', ')}</span>
                          )}
                        </td>
                        <td className="p-3 font-mono text-sm text-gray-700">{b.pos || ''}</td>
                        <td className="p-3 text-center font-mono text-sm text-gray-700">{b.units || 1}</td>
                        <td className="p-3 text-right font-mono text-sm">
                          {b.charges ? `$${Number(b.charges).toFixed(2)}` : ''}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              onClick={() => handleSendNoteBackForCorrections(note)}
                              variant="ghost"
                              size="sm"
                              className="text-orange-600 hover:bg-orange-50"
                              disabled={sendingBackNote === note.id}
                              title="Send note back to provider for corrections"
                              data-testid={`sendback-note-${note.id}`}
                            >
                              <CornerDownLeft className="h-3 w-3" />
                            </Button>
                            <Button
                              onClick={() => {
                                const realIndex = filteredNotes.indexOf(note);
                                setCurrentNoteIndex(realIndex >= 0 ? realIndex : index);
                                setSelectedNoteForReview(note);
                                // Jun 2026 — pass the biller's sorted view for the
                                // V2 host pager (legacy ignores the extra arg).
                                fetchNoteForReview(note.id, {
                                  index: sortedNotes.findIndex(n => n.id === note.id),
                                  list: sortedNotes,
                                });
                              }}
                              variant="outline"
                              size="sm"
                              className={isSelfPay 
                                ? "border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                                : "border-blue-600 text-blue-600 hover:bg-blue-50"}
                              data-testid={`prepare-note-${note.id}`}
                            >
                              {isSelfPay ? (
                                <><CreditCard className="h-3 w-3 mr-1" />Process</>
                              ) : (
                                <><Sparkles className="h-3 w-3 mr-1" />Prepare</>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleMoveToCorrections(note)}
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:bg-amber-50"
                              disabled={movingToCorrection === note.id}
                              title="Create claim draft & move to Needs Corrections"
                              data-testid={`correction-note-${note.id}`}
                            >
                              <CornerDownRight className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* Card View */
            <div className="space-y-3">
              {notesPagination.paginatedItems.map((note, index) => {
                const noteProvider = note._provider_name || users.find(u => u.id === note.provider_id)?.full_name || providers.find(p => p.id === note.provider_id)?.full_name;
                const b = note._billing || {};
                const isSelfPay = note._client_is_self_pay !== false && (!note._client_insurance || note._client_insurance === 'Self Pay');
                
                return (
                  <Card key={note.id} className="p-4 bg-amber-50/50 border-amber-100">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div>
                            <p className="text-[10px] text-gray-400 font-mono leading-tight">{note._client_mrn || ''}</p>
                            <p className="font-semibold text-gray-900">
                              {formatClientName(note._client_name || note.client_name)}
                            </p>
                          </div>
                          {b.cpt_code ? (
                            <Badge data-testid="card-cpt-badge" className="bg-blue-100 text-blue-800 font-mono text-xs">
                              {b.cpt_code}{b.modifiers?.length > 0 ? ` (${b.modifiers.join(', ')})` : ''}
                            </Badge>
                          ) : (
                            <Badge data-testid="card-no-cpt-badge" className="bg-amber-100 text-amber-700 text-xs">No CPT</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <div>
                            <span className="text-gray-600">Provider:</span>
                            <span className="ml-2 font-medium">{noteProvider || 'Unknown'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Insurance:</span>
                            <span className="ml-2" title={note._client_insurance || 'Self Pay'}>
                              {isSelfPay ? <span className="text-emerald-700 font-medium">Self Pay</span> : shortInsName(note._client_insurance) || <span className="text-gray-400">—</span>}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Date:</span>
                            <span className="ml-2">{formatDateStringForDisplay(note.date_of_service)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Note:</span>
                            <span className="ml-2">
                              {note.title || note.template_name || note.form_name || 'Untitled'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">POS:</span>
                            <span className="ml-2 font-mono">{b.pos || '—'}</span>
                            <span className="ml-3 text-gray-600">Units:</span>
                            <span className="ml-1 font-mono">{b.units || 1}</span>
                            {b.charges ? <span className="ml-3 text-gray-600">${Number(b.charges).toFixed(2)}</span> : null}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setCurrentNoteIndex(index);
                          setSelectedNoteForReview(note);
                          // Jun 2026 — pass the biller's sorted view so the
                          // V2 host's Next/Prev pager mirrors the screen
                          // (legacy App.js ignores the extra arg).
                          fetchNoteForReview(note.id, {
                            index: sortedNotes.findIndex(n => n.id === note.id),
                            list: sortedNotes,
                          });
                        }}
                        variant="outline"
                        className={isSelfPay 
                          ? "border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          : "border-blue-600 text-blue-600 hover:bg-blue-50"}
                      >
                        {isSelfPay ? (
                          <><CreditCard className="h-4 w-4 mr-2" />Process Bill</>
                        ) : (
                          <><Sparkles className="h-4 w-4 mr-2" />Prepare Claim</>
                        )}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          <Pagination
            totalItems={notesPagination.totalItems}
            currentPage={notesPagination.page}
            pageSize={notesPagination.pageSize}
            onPageChange={notesPagination.setPage}
            onPageSizeChange={notesPagination.setPageSize}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
          />
        </CardContent>
        )}
      </Card>

      {/* Claims Needing Corrections Section */}
      <Card className="border-l-4 border-l-red-500" data-testid="claims-needing-correction-section">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('claimsCorrections')}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ChevronDown className={`h-4 w-4 transition-transform ${sectionsCollapsed.claimsCorrections ? '-rotate-90' : ''}`} />
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Claims Awaiting Corrections
            </span>
            <Badge className="bg-red-600 text-white">
              {filteredClaimsCorrections.length} claims
            </Badge>
          </CardTitle>
          <p className="text-sm text-gray-600">Claims awaiting corrections before rebilling</p>
        </CardHeader>
        {!sectionsCollapsed.claimsCorrections && (
        <CardContent>
          {loadingCorrections ? (
            <div className="text-center py-6 text-gray-500">Loading...</div>
          ) : filteredClaimsCorrections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No claims pending corrections</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="claims-correction-table">
                <thead>
                  <tr className="bg-red-50 text-left">
                    <th className="p-2 font-semibold">Client</th>
                    <th className="p-2 font-semibold">Ins</th>
                    <th className="p-2 font-semibold">Provider</th>
                    <th className="p-2 font-semibold">DOS</th>
                    <th className="p-2 font-semibold" title="How long until the timely-filing window closes">Deadline</th>
                    <th className="p-2 font-semibold">CPT</th>
                    <th className="p-2 font-semibold text-center">Units</th>
                    <th className="p-2 font-semibold">Charges</th>
                    <th className="p-2 font-semibold">Correction Notes</th>
                    <th className="p-2 font-semibold">Returned</th>
                    <th className="p-2 font-semibold text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {correctionsPagination.paginatedItems.map((claim) => (
                    <tr key={claim.id} className="border-b hover:bg-red-50/50" data-testid={`correction-claim-${claim.id}`}>
                      <td className="p-2">
                        <div className="text-[10px] text-gray-400 font-mono leading-tight">{claim.client_mrn || ''}</div>
                        <div className="font-medium">{formatClientName(claim.client_name)}</div>
                      </td>
                      <td className="p-2 text-xs text-gray-700" title={claim.primary_insurance || ''}>
                        {claim.is_self_pay || claim.primary_insurance === 'Self Pay' || !claim.primary_insurance 
                          ? <span className="text-emerald-700 font-medium">Self Pay</span> 
                          : shortInsName(claim.primary_insurance)}
                      </td>
                      <td className="p-2">{claim.provider_name || 'N/A'}</td>
                      <td className="p-2">{formatDateStringForDisplay(claim.date_of_service)}</td>
                      <td className="p-2">
                        <BillingDeadlineBadge
                          dos={claim.date_of_service}
                          daysUntilDeadline={typeof claim.days_until_deadline === 'number' ? claim.days_until_deadline : null}
                          explicitDeadlineDate={claim.current_deadline || null}
                          payerName={claim.primary_insurance || null}
                        />
                      </td>
                      <td className="p-2">
                        {(claim.service_lines || []).map((sl, i) => (
                          <Badge key={i} variant="outline" className="mr-1 text-xs bg-blue-50 text-blue-700 border-blue-200">
                            {sl.cpt_code}{sl.modifiers?.length > 0 ? `:${sl.modifiers.join(',')}` : ''}
                          </Badge>
                        ))}
                      </td>
                      <td className="p-2 text-center font-mono text-sm">
                        {(claim.service_lines || []).reduce((sum, sl) => sum + (sl.units || 1), 0)}
                      </td>
                      <td className="p-2 font-medium">${(claim.total_charged_amount || 0).toFixed(2)}</td>
                      <td className="p-2">
                        {claim.correction_notes ? (
                          <span className="text-red-700 text-xs">{claim.correction_notes}</span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-2 text-xs text-gray-500">
                        {claim.returned_at ? new Date(claim.returned_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs border-blue-500 text-blue-600" data-testid={`edit-correction-${claim.id}`}
                            onClick={() => {
                              if (claim.note_id) {
                                fetchNoteForReview(claim.note_id);
                              } else {
                                toast.error('No linked note found for this claim');
                              }
                            }}>
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs border-green-500 text-green-600" data-testid={`resubmit-correction-${claim.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Mark claim for ${claim.client_name} as ready for billing?`)) return;
                              try {
                                await axios.patch(`${API}/claims/${claim.id}/billing-fields`, { status: 'ready_for_billing' });
                                toast.success('Claim marked ready for billing');
                                fetchClaimsNeedingCorrection();
                                fetchClaimsQueue();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to update claim');
                              }
                            }}>
                            <Send className="h-3 w-3 mr-1" /> Ready
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filteredClaimsCorrections.length > 0 && (
            <Pagination
              totalItems={correctionsPagination.totalItems}
              currentPage={correctionsPagination.page}
              pageSize={correctionsPagination.pageSize}
              onPageChange={correctionsPagination.setPage}
              onPageSizeChange={correctionsPagination.setPageSize}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
            />
          )}
        </CardContent>
        )}
      </Card>

      {/* Sent for Corrections Section */}
      <Card className="border-l-4 border-l-orange-600">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('notesSentBack')}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ChevronDown className={`h-4 w-4 transition-transform ${sectionsCollapsed.notesSentBack ? '-rotate-90' : ''}`} />
              Notes Sent Back for Corrections
            </span>
            <Badge className="bg-orange-600 text-white">
              {filteredNotesSentBack.length} notes
            </Badge>
          </CardTitle>
          <p className="text-sm text-gray-600">Notes sent back to providers for revisions</p>
        </CardHeader>
        {!sectionsCollapsed.notesSentBack && (
        <CardContent>
          {filteredNotesSentBack.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No notes pending corrections</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="notes-sent-back-table">
                <thead>
                  <tr className="bg-orange-50 text-left">
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('client')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-client"
                      >
                        Client <SentBackSortIndicator column="client" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('insurance')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-insurance"
                      >
                        Ins <SentBackSortIndicator column="insurance" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('provider')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-provider"
                      >
                        Provider <SentBackSortIndicator column="provider" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('dos')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-dos"
                      >
                        DOS <SentBackSortIndicator column="dos" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('note')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-note"
                      >
                        Note <SentBackSortIndicator column="note" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('reason')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-reason"
                      >
                        Correction Reason <SentBackSortIndicator column="reason" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => toggleSentBackSort('sent_back')}
                        className="inline-flex items-center hover:text-orange-700"
                        data-testid="sent-back-sort-sent-back"
                      >
                        Sent Back <SentBackSortIndicator column="sent_back" />
                      </button>
                    </th>
                    <th className="p-2 font-semibold text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sentBackPagination.paginatedItems.map((note) => {
                    const noteProvider = note._provider_name || users.find(u => u.id === note.provider_id)?.full_name || providers.find(p => p.id === note.provider_id)?.full_name;
                    const isSelfPay = note._client_is_self_pay !== false && (!note._client_insurance || note._client_insurance === 'Self Pay');
                    return (
                      <tr key={note.id} className="border-b hover:bg-orange-50/50" data-testid={`sent-back-note-${note.id}`}>
                        <td className="p-2">
                          <div className="text-[10px] text-gray-400 font-mono leading-tight">{note._client_mrn || ''}</div>
                          <div className="font-medium">{formatClientName(note._client_name || note.client_name)}</div>
                        </td>
                        <td className="p-2 text-xs text-gray-700" title={note._client_insurance || 'Self Pay'}>
                          {isSelfPay ? <span className="text-emerald-700 font-medium">Self Pay</span> : shortInsName(note._client_insurance) || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="p-2">{noteProvider || 'Unknown'}</td>
                        <td className="p-2 text-xs">{formatDateStringForDisplay(note.date_of_service)}</td>
                        <td className="p-2 text-xs max-w-[120px] truncate" title={note.title || note.template_name || note.form_name || ''}>
                          {note.title || note.template_name || note.form_name || 'Untitled'}
                        </td>
                        <td className="p-2 text-xs max-w-[200px]">
                          <span className="text-red-700">{note.needs_corrections_reason || note.biller_notes || '—'}</span>
                        </td>
                        <td className="p-2 text-xs text-gray-500">{note.updated_at ? new Date(note.updated_at).toLocaleDateString() : 'N/A'}</td>
                        <td className="p-2 text-center">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-blue-600 border-blue-500"
                            onClick={() => { if (onViewNote) onViewNote(note); }}>
                            <Eye className="h-3 w-3 mr-1" /> View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {filteredNotesSentBack.length > 0 && (
            <Pagination
              totalItems={sentBackPagination.totalItems}
              currentPage={sentBackPagination.page}
              pageSize={sentBackPagination.pageSize}
              onPageChange={sentBackPagination.setPage}
              onPageSizeChange={sentBackPagination.setPageSize}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
            />
          )}
        </CardContent>
        )}
      </Card>

      {/* Universal Claim Viewer */}
      <ClaimViewer
        claimId={viewingClaimId}
        open={!!viewingClaimId}
        onClose={() => setViewingClaimId(null)}
        clientName={viewingClaimName}
        onClaimRebuilt={() => {
          fetchClaimsQueue();
          fetchClaimsNeedingCorrection();
        }}
      />

      {/* Legacy Billing Transition — Confirm Dialog */}
      <Dialog open={showLegacyMarkDialog} onOpenChange={(o) => !legacyMarkInProgress && setShowLegacyMarkDialog(o)}>
        <DialogContent className="sm:max-w-[520px]" data-testid="legacy-mark-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-purple-700" />
              Mark as Billed in Legacy System
            </DialogTitle>
            <DialogDescription>
              Creates a stub claim per selected note (status <code className="bg-purple-100 px-1 rounded">billed_legacy</code>)
              so ERA payments can be matched back to the services. The original notes will be locked.
              This does <strong>not</strong> generate an 837P or submit anything to a payer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="p-2 bg-gray-50 border rounded text-sm">
              <span className="font-medium">{selectedNoteIds.size}</span> note{selectedNoteIds.size > 1 ? 's' : ''} will be marked as billed in the prior system.
            </div>

            <div>
              <Label htmlFor="legacy-system-name" className="text-sm">Legacy system name <span className="text-gray-400 text-xs">(optional)</span></Label>
              <Input
                id="legacy-system-name"
                placeholder="e.g., TherapyNotes, SimplePractice, AdvancedMD"
                value={legacySystemName}
                onChange={(e) => setLegacySystemName(e.target.value)}
                data-testid="legacy-system-name-input"
              />
            </div>

            <div>
              <Label htmlFor="legacy-submission-date" className="text-sm">Original submission date <span className="text-gray-400 text-xs">(optional — for timely-filing tracking)</span></Label>
              <Input
                id="legacy-submission-date"
                type="date"
                value={legacySubmissionDate}
                onChange={(e) => setLegacySubmissionDate(e.target.value)}
                data-testid="legacy-submission-date-input"
              />
            </div>

            <div>
              <Label htmlFor="legacy-ref-number" className="text-sm">Legacy reference number <span className="text-gray-400 text-xs">(optional — applied to all selected)</span></Label>
              <Input
                id="legacy-ref-number"
                placeholder="e.g., BATCH-2026-03"
                value={legacyRefNumber}
                onChange={(e) => setLegacyRefNumber(e.target.value)}
                data-testid="legacy-ref-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Same reference is stamped on every stub in this batch. Leave blank if you don&apos;t have per-note reference numbers.
              </p>
            </div>

            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Default CPT &amp; charge <span className="font-normal text-gray-400 normal-case">(for notes that have no CPT set)</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="legacy-default-cpt" className="text-sm">Default CPT code</Label>
                  <Input
                    id="legacy-default-cpt"
                    placeholder="e.g., 90834"
                    value={legacyDefaultCptCode}
                    onChange={(e) => setLegacyDefaultCptCode(e.target.value)}
                    data-testid="legacy-default-cpt-input"
                  />
                </div>
                <div>
                  <Label htmlFor="legacy-default-charge" className="text-sm">Default charge ($)</Label>
                  <Input
                    id="legacy-default-charge"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g., 120.00"
                    value={legacyDefaultCharge}
                    onChange={(e) => setLegacyDefaultCharge(e.target.value)}
                    data-testid="legacy-default-charge-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Only applied when a note has no CPT/appointment. Notes that already have a CPT keep theirs.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLegacyMarkDialog(false)}
              disabled={legacyMarkInProgress}
              data-testid="legacy-mark-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={submitLegacyBulkMark}
              disabled={legacyMarkInProgress || selectedNoteIds.size === 0}
              data-testid="legacy-mark-confirm"
            >
              {legacyMarkInProgress ? (
                <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Creating stubs…</>
              ) : (
                <><Check className="h-4 w-4 mr-1" /> Create {selectedNoteIds.size} stub claim{selectedNoteIds.size > 1 ? 's' : ''}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default BillingTab;
